import { Controller } from "@hotwired/stimulus"

// Copy buttons for a group of values: writes the button's data-clipboard-text
// to the clipboard and marks that button as copied for a moment. Only one button in
// the group shows the copied state at a time, and the status target announces
// the outcome to screen readers -- including a failed copy (no clipboard on a
// non-secure origin, permission denied).
export default class extends Controller {
  static targets = ["button", "status"]
  static values = { copiedText: String, failedText: String }

  static COPIED_FOR_MS = 1400
  // Screen readers skip a live region whose text is cleared and set back to
  // the same words in one go, so a second copy would go unannounced.
  static ANNOUNCE_AFTER_MS = 100

  disconnect() {
    clearTimeout(this.timeout)
    clearTimeout(this.announceTimeout)
  }

  // Read raw rather than as an action param: Stimulus JSON-parses params, so a
  // reference like "1.50" or "1e5" would be copied as 1.5 or 100000.
  async copy({ currentTarget }) {
    this.reset()

    try {
      await navigator.clipboard.writeText(currentTarget.dataset.clipboardText)
    } catch {
      this.announce(this.failedTextValue)
      return
    }

    currentTarget.dataset.copied = ""
    this.announce(this.copiedTextValue)
    this.timeout = setTimeout(() => this.reset(), this.constructor.COPIED_FOR_MS)
  }

  reset() {
    clearTimeout(this.timeout)
    clearTimeout(this.announceTimeout)
    this.buttonTargets.forEach((button) => delete button.dataset.copied)
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
  }

  announce(message) {
    if (!this.hasStatusTarget) return

    this.announceTimeout = setTimeout(() => {
      this.statusTarget.textContent = message
    }, this.constructor.ANNOUNCE_AFTER_MS)
  }
}
